import { useId, useMemo, type ReactNode } from "react";
import type { ObservablePerson } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { observablePeopleQuery } from "../api/queries";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { Chip } from "../design/components/Chip";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { PageHeader } from "../design/components/PageHeader";
import { Reason } from "../design/components/Reason";
import { messageForError } from "../i18n/errors";
import { useLocale, useT } from "../i18n/locale";
import { pageInMenu } from "../routes/rail-items";
import type { Access, MenuSubject } from "../session/access";
import { changeCommercialView } from "../session/switch-tab";
import type { CommercialView, TabContext } from "../session/tab-context";
import "./commercial-view.css";

// «Guarda come…», la Vista Commerciale nella shell (piano M2 Task 22, D16; decisione confermata «osservazione senza
// impersonazione»): il selettore si apre dalla rail, la fascia resta in cima a ogni pagina finché la scheda guarda come
// un'altra persona, con «Cambia» ed «Esci». La vista vive nella scheda del browser (`tab-context.ts`) e viaggia con ogni
// richiesta; cambiarla o uscirne è una navigazione (`/cambia-vista`) che svuota la cache della scheda. Il server decide a
// ogni richiesta: l'interfaccia offre il selettore soltanto a chi ha la funzione `commercial_view` (il contesto di
// piattaforma e il ruolo di sistema le hanno tutte), e le persone che il selettore elenca le sceglie il server con la
// stessa regola dell'header (`GET /api/commercial-view/people`).
//
// Dal 2 ottobre 2026 (decisioni del product owner) si guarda qualunque persona attiva dell'azienda, di qualunque ruolo,
// purché non abbia più poteri di chi guarda; durante la vista il menu è quello della persona scelta: una pagina fuori dal
// suo menu, aperta da un link o scritta nell'indirizzo, non si apre e lo dice (`CommercialViewGate`).

/** Chi può aprire «Guarda come…»: la funzione `commercial_view` del contesto della scheda. */
export function canUseCommercialView(access: Access): boolean {
  return access.can("commercial_view");
}

type Group = { key: string; title: string; people: ObservablePerson[] };

/**
 * I gruppi del selettore, nell'ordine del server: un gruppo per ogni ruolo di partenza (Super admin, Admin, Super user,
 * User, con il nome che l'azienda gli ha dato), poi «Altri ruoli» con i ruoli su misura, ruolo dopo ruolo. Dentro un
 * ruolo le persone in ordine di nome, nella lingua di chi guarda.
 */
function groupsOf(people: readonly ObservablePerson[], otherRoles: string, locale: string): Group[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const roleRank = new Map<string, number>();
  for (const person of people) if (!roleRank.has(person.role.id)) roleRank.set(person.role.id, roleRank.size);
  const sorted = [...people].sort(
    (left, right) =>
      (roleRank.get(left.role.id) ?? 0) - (roleRank.get(right.role.id) ?? 0) ||
      collator.compare(left.displayName, right.displayName) ||
      (left.membershipId < right.membershipId ? -1 : 1),
  );
  const groups: Group[] = [];
  for (const person of sorted) {
    const key = person.role.isCustom ? "other" : `role:${person.role.id}`;
    let group = groups.find((candidate) => candidate.key === key);
    if (!group) {
      group = { key, title: person.role.isCustom ? otherRoles : person.role.name, people: [] };
      groups.push(group);
    }
    group.people.push(person);
  }
  return groups;
}

type SelectorProps = { tab: TabContext; onClose: () => void };

/**
 * «Guarda come un'altra persona dell'azienda»: le persone che chi guarda può scegliere, raggruppate per ruolo, ognuna con
 * nome e ruolo; quella già in vista è segnata. Scelta una persona, la finestra si chiude e la scheda cambia vista restando
 * nella stessa sezione (una bozza non salvata può fermare il cambio, come per l'azienda).
 */
export function CommercialViewSelector({ tab, onClose }: SelectorProps) {
  const t = useT();
  const { locale } = useLocale();
  const router = useRouter();
  const baseId = useId();
  const people = useQuery(observablePeopleQuery(tab));
  const current = tab.commercialView?.membershipId ?? null;
  const otherRoles = t("shell.commercialView.otherRoles");
  const groups = useMemo(() => (people.data ? groupsOf(people.data, otherRoles, locale) : null), [people.data, otherRoles, locale]);

  function choose(next: CommercialView | null) {
    onClose();
    if ((next?.membershipId ?? null) === current) return;
    void changeCommercialView(router, next);
  }

  let content: ReactNode;
  if (people.isError) {
    content = (
      <div className="g-view-picker__problem">
        <Reason tone="warning" role="alert">{messageForError(t, people.error)}</Reason>
        <Button onClick={() => void people.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!groups) {
    content = <p>{t("common.loading")}</p>;
  } else if (groups.length === 0) {
    content = <p>{t("shell.commercialView.none")}</p>;
  } else {
    content = (
      <div className="g-view-picker__groups" role="group" aria-label={t("shell.commercialView.list")}>
        {groups.map((group, index) => {
          const titleId = `${baseId}-group-${index}`;
          return (
            <section key={group.key} className="g-view-picker__group" aria-labelledby={titleId}>
              <h3 id={titleId} className="g-view-picker__title">
                {group.title}
              </h3>
              <ul className="g-view-picker" aria-labelledby={titleId}>
                {group.people.map((person) => (
                  <li key={person.membershipId} className="g-view-picker__item">
                    <button
                      type="button"
                      className="g-view-picker__choice"
                      aria-current={person.membershipId === current ? "true" : undefined}
                      onClick={() => choose({ membershipId: person.membershipId, displayName: person.displayName })}
                    >
                      <Icon name="user" />
                      <span className="g-view-picker__name">{person.displayName}</span>{" "}
                      <span className="g-view-picker__role">{person.role.name}</span>
                    </button>
                    {person.membershipId === current ? <Chip tone="accent">{t("shell.commercialView.current")}</Chip> : null}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <Dialog
      open
      title={t("shell.commercialView.title")}
      onClose={onClose}
      footer={
        <>
          {current ? (
            <Button variant="ghost" onClick={() => choose(null)}>
              {t("shell.commercialView.exit")}
            </Button>
          ) : null}
          <span className="g-view-picker__spacer" />
          <Button onClick={onClose}>{t("common.cancel")}</Button>
        </>
      }
    >
      <div className="g-stack">
        <p>{t("shell.commercialView.intro")}</p>
        {content}
      </div>
    </Dialog>
  );
}

type BandProps = { view: CommercialView; role: string | null; canChange: boolean; onChange: () => void };

/**
 * La fascia della vista, sempre in vista in cima al contenuto: chi si guarda e con quale ruolo («Stai guardando come vede
 * Sara Conti (User).»), che cosa vuol dire, «Cambia» (se si può ancora scegliere) ed «Esci». Il ruolo è quello che il
 * contesto della vista porta; finché non c'è, soltanto il nome.
 */
export function CommercialViewBand({ view, role, canChange, onChange }: BandProps) {
  const t = useT();
  const router = useRouter();
  return (
    <section className="g-view-band" aria-label={t("shell.commercialView.region")}>
      <Icon name="eye" size="md" />
      <p className="g-view-band__text">
        <strong>{role ? t("shell.commercialView.band", { name: view.displayName, role }) : t("shell.commercialView.bandName", { name: view.displayName })}</strong>
        <span>{t("shell.commercialView.bandNote")}</span>
      </p>
      <div className="g-view-band__actions">
        {canChange ? <Button onClick={onChange}>{t("shell.commercialView.change")}</Button> : null}
        <Button variant="primary" aria-label={t("shell.commercialView.exit")} onClick={() => void changeCommercialView(router, null)}>
          {t("shell.commercialView.leave")}
        </Button>
      </div>
    </section>
  );
}

/**
 * L'avviso, sulla pagina riaperta, quando il server non accetta più la vista della scheda (la persona sospesa, il suo
 * ruolo ora con più poteri di chi guarda, la funzione tolta): la guardia o il gestore degli errori delle query l'hanno
 * chiusa (`router.tsx`).
 */
export function CommercialViewClosed() {
  const t = useT();
  const closed = useRouterState({ select: (state) => state.location.state.viewClosed });
  if (typeof closed !== "string" || closed === "") return null;
  return (
    <Reason tone="warning" role="status">
      {t("shell.commercialView.closed", { name: closed })}
    </Reason>
  );
}

type GateProps = { view: CommercialView | null; subject: MenuSubject; children: ReactNode };

/**
 * Durante «Guarda come…» apre soltanto le pagine del menu della persona scelta (`pageInMenu`); per le altre, per esempio
 * «Aziende di Gioia365» o una sezione delle Impostazioni che lei non ha, mostra l'avviso con «Esci dalla vista», e la
 * pagina non si disegna: nessun suo dato si legge. Decide sulla pagina disegnata adesso (l'ultima
 * rotta della navigazione conclusa), non sull'indirizzo di una navigazione ancora in corso, così una pagina fuori dal
 * menu non si disegna mai, nemmeno per un attimo. Fuori dalla vista non cambia nulla.
 */
export function CommercialViewGate({ view, subject, children }: GateProps) {
  const pathname = useRouterState({ select: (state) => state.matches.at(-1)?.pathname ?? state.location.pathname });
  if (!view || pageInMenu(pathname, subject)) return children;
  return <OutsideCommercialView view={view} />;
}

/** L'avviso al posto di una pagina fuori dal menu della vista; «Esci dalla vista» riapre la stessa pagina. */
function OutsideCommercialView({ view }: { view: CommercialView }) {
  const t = useT();
  const router = useRouter();
  return (
    <section className="g-view-outside">
      <PageHeader eyebrow={t("shell.commercialView.open")} title={t("shell.commercialView.outsideTitle")} />
      <Card className="g-stack">
        <p>{t("shell.commercialView.outside", { name: view.displayName })}</p>
        <div className="g-view-outside__actions">
          <Button variant="primary" onClick={() => void changeCommercialView(router, null)}>
            {t("shell.commercialView.exit")}
          </Button>
        </div>
      </Card>
    </section>
  );
}
